import type { ErrorResponse } from '@nuxtjs/apollo'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import * as Sentry from '@sentry/nuxt'
import { hash } from 'ohash'
import { account } from '~/store/account'
// TODO: Move this to the types folder
class GraphQlError extends Error {
    constructor (errorCategory: string, message: string) {
        super(message)
        const hashedMessage = hash(message) // We are hashing the message to make sure that Sentry can distinguish between different errors within the same GQL query
        this.name = `GraphQLError - ${errorCategory} (${hashedMessage})`
    }
}
// TODO: move these functions to the utils folder
export function hasValidation (error: ErrorResponse) {
    return error?.graphQLErrors?.length && error?.graphQLErrors[0]?.extensions?.validation
}
function isUnauthenticated (error: ErrorResponse) {
    return error?.graphQLErrors?.length && error?.graphQLErrors[0]?.message === 'Unauthenticated.'
}
function getReadableErrorMessage (message: string) {
    return message.includes('got invalid value') && message.includes(';') ? message.split(';')[1] : message
}
function getErrorCategory (error: ErrorResponse) {
    return error.operation.operationName || 'unknown'
}
function throwSentryError (error: ErrorResponse) {
    const errorCategory = getErrorCategory(error)
    const firstGQLError = error?.graphQLErrors?.[0]
    Sentry.captureException(new GraphQlError(errorCategory, firstGQLError ? getReadableErrorMessage(firstGQLError.message) : 'Unknown error'))
}
function addSentryTags (error: ErrorResponse) {
    const traceHeader = error?.operation.getContext()?.response?.headers?.get('X-Amzn-Trace-Id')
    if (traceHeader) {
        Sentry.setTag('X-Amzn-Trace-Id', traceHeader)
    }
}
function addSentryBreadcrumbs (error: ErrorResponse) {
    const errorCategory = getErrorCategory(error)
    Sentry.addBreadcrumb({
        type: 'GQL',
        category: `${errorCategory} - Operation Variables`,
        level: 'info',
        data: error.operation.variables
    })
    Sentry.addBreadcrumb({
        type: 'GQL',
        category: `${errorCategory} - Operation Query`,
        level: 'info',
        data: error.operation.query
    })
    error?.graphQLErrors?.forEach((err, index) => {
        Sentry.addBreadcrumb({
            type: 'GQL',
            category: `${errorCategory} - Error Message (${index})`,
            message: getReadableErrorMessage(err.message),
            level: 'warning',
            data: err
        })
    })
    if (error.networkError) {
        Sentry.addBreadcrumb({
            type: 'GQL',
            category: `${errorCategory} - Network Error`,
            message: error.networkError.message,
            level: 'error',
            data: error.networkError
        })
    }
}

function handleGraphQLError (error: ErrorResponse) {
    addSentryTags(error) // This will add tags to the Sentry event
    addSentryBreadcrumbs(error) // This will add debugging information to the Sentry event breadcrumbs
    throwSentryError(error) // This will throw a GraphQLError and send it to Sentry
    throw showError({ statusCode: 500, statusMessage: 'Handled GraphQL error', data: error }) // This will show a generic error message to the user but we do not send it to Sentry (see sentry.client.config.ts)
}

export default defineNuxtPlugin(nuxtApp => {
    if (process.env.ENV === 'local') {
        loadDevMessages()
        loadErrorMessages()
    }
    nuxtApp.hook('apollo:error', error => {
        if (isUnauthenticated(error)) {
            account().logoutUser(true)
        } else if (!hasValidation(error)) {
            handleGraphQLError(error)
        }
    })
})
